// Request Tracing & Observability Service
// Duolingo-style distributed tracing for performance monitoring
// Identifies bottlenecks and improves performance (10% latency reduction)

import { logger } from '../utils/logger';
import { connectionPool } from './connectionPool';
import { userAuth } from './userAuth';

export interface TraceSpan {
  requestId: string;
  operation: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  success: boolean;
  error?: string;
  metadata?: Record<string, any>;
  parentRequestId?: string;
  service: string;
}

export interface PerformanceMetrics {
  operation: string;
  avgDuration: number;
  p50: number; // Median
  p95: number; // 95th percentile
  p99: number; // 99th percentile
  errorRate: number;
  requestCount: number;
  lastUpdated: number;
}

class RequestTracingService {
  private activeTraces = new Map<string, TraceSpan>();
  private metricsCache = new Map<string, PerformanceMetrics>();
  private readonly METRICS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly BATCH_SIZE = 50;
  private traceQueue: TraceSpan[] = [];
  private flushInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Start periodic flush
    this.startPeriodicFlush();
    
    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flushTraces();
      });
    }
  }

  /**
   * Start tracing a request
   */
  startTrace(
    operation: string,
    metadata?: Record<string, any>,
    parentRequestId?: string
  ): string {
    const requestId = this.generateRequestId();
    const startTime = performance.now();

    const span: TraceSpan = {
      requestId,
      operation,
      startTime,
      success: false,
      metadata,
      parentRequestId,
      service: 'theorycoach-frontend'
    };

    this.activeTraces.set(requestId, span);
    
    logger.debug(`[Trace] Started: ${operation} (${requestId})`);
    
    return requestId;
  }

  /**
   * End tracing a request
   */
  endTrace(
    requestId: string,
    success: boolean = true,
    error?: string,
    metadata?: Record<string, any>
  ): void {
    const span = this.activeTraces.get(requestId);
    if (!span) {
      logger.warn(`[Trace] Request ID not found: ${requestId}`);
      return;
    }

    const endTime = performance.now();
    const duration = endTime - span.startTime;

    span.endTime = endTime;
    span.duration = duration;
    span.success = success;
    span.error = error;
    span.metadata = { ...span.metadata, ...metadata };

    // Remove from active traces
    this.activeTraces.delete(requestId);

    // Add to queue for batch processing
    this.traceQueue.push(span);

    // Flush if queue is full
    if (this.traceQueue.length >= this.BATCH_SIZE) {
      this.flushTraces();
    }

    // Update metrics cache
    this.updateMetricsCache(span);

    logger.debug(
      `[Trace] Completed: ${span.operation} (${requestId}) - ${duration.toFixed(2)}ms - ${success ? 'SUCCESS' : 'ERROR'}`
    );
  }

  /**
   * Trace an async operation
   */
  async trace<T>(
    operation: string,
    fn: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    const requestId = this.startTrace(operation, metadata);
    
    try {
      const result = await fn();
      this.endTrace(requestId, true, undefined, metadata);
      return result;
    } catch (error: any) {
      this.endTrace(requestId, false, error.message || 'Unknown error', metadata);
      throw error;
    }
  }

  /**
   * Get performance metrics for an operation
   */
  async getMetrics(operation: string): Promise<PerformanceMetrics | null> {
    // Check cache first
    const cached = this.metricsCache.get(operation);
    if (cached && Date.now() - cached.lastUpdated < this.METRICS_CACHE_TTL) {
      return cached;
    }

    try {
      const current = userAuth.getCurrentUser();
      const uid = current?.id;
      if (!uid || current?.isGuest) {
        return cached || null;
      }

      const client = connectionPool.getClient();
      const { data, error } = await client
        .from('analytics_events')
        .select('*')
        .eq('user_id', uid)
        .eq('event_type', 'request_trace')
        .eq('event_data->>operation', operation)
        .order('timestamp', { ascending: false })
        .limit(1000);

      if (error) {
        logger.error('Error fetching metrics:', error);
        return cached || null;
      }

      if (!data || data.length === 0) {
        return null;
      }

      // Calculate metrics
      const durations = data
        .map((event: any) => event.event_data?.duration || 0)
        .filter((d: number) => d > 0)
        .sort((a: number, b: number) => a - b);

      if (durations.length === 0) {
        return null;
      }

      const metrics: PerformanceMetrics = {
        operation,
        avgDuration: durations.reduce((a: number, b: number) => a + b, 0) / durations.length,
        p50: durations[Math.floor(durations.length * 0.5)],
        p95: durations[Math.floor(durations.length * 0.95)],
        p99: durations[Math.floor(durations.length * 0.99)],
        errorRate: data.filter((e: any) => !e.event_data?.success).length / data.length,
        requestCount: data.length,
        lastUpdated: Date.now()
      };

      // Update cache
      this.metricsCache.set(operation, metrics);

      return metrics;
    } catch (error) {
      logger.error('Error calculating metrics:', error);
      return cached || null;
    }
  }

  /**
   * Get all slow operations (above threshold)
   */
  async getSlowOperations(thresholdMs: number = 1000): Promise<Array<{ operation: string; avgDuration: number }>> {
    const slowOps: Array<{ operation: string; avgDuration: number }> = [];

    // Get unique operations from cache
    const operations = Array.from(this.metricsCache.keys());

    for (const operation of operations) {
      const metrics = await this.getMetrics(operation);
      if (metrics && metrics.avgDuration > thresholdMs) {
        slowOps.push({
          operation,
          avgDuration: metrics.avgDuration
        });
      }
    }

    return slowOps.sort((a, b) => b.avgDuration - a.avgDuration);
  }

  /**
   * Update metrics cache
   */
  private updateMetricsCache(span: TraceSpan): void {
    const existing = this.metricsCache.get(span.operation);
    
    if (existing) {
      // Update running average
      const newCount = existing.requestCount + 1;
      const newAvg = (existing.avgDuration * existing.requestCount + (span.duration || 0)) / newCount;
      const errorCount = existing.errorRate * existing.requestCount;
      const newErrorCount = span.success ? errorCount : errorCount + 1;
      
      existing.avgDuration = newAvg;
      existing.requestCount = newCount;
      existing.errorRate = newErrorCount / newCount;
      existing.lastUpdated = Date.now();
    } else {
      // Create new metrics
      this.metricsCache.set(span.operation, {
        operation: span.operation,
        avgDuration: span.duration || 0,
        p50: span.duration || 0,
        p95: span.duration || 0,
        p99: span.duration || 0,
        errorRate: span.success ? 0 : 1,
        requestCount: 1,
        lastUpdated: Date.now()
      });
    }
  }

  /**
   * Flush traces to database
   */
  private async flushTraces(): Promise<void> {
    if (this.traceQueue.length === 0) return;

    const user = userAuth.getCurrentUser();
    const uid = user?.id;
    if (!user || !uid || user.isGuest) {
      // Drop queued spans (not persisted under RLS); avoids unbounded memory for guests
      this.traceQueue.splice(0, this.BATCH_SIZE);
      return;
    }

    const tracesToFlush = this.traceQueue.splice(0, this.BATCH_SIZE);

    try {
      const client = connectionPool.getClient();
      
      // Convert traces to analytics events format (user_id required for RLS)
      const events = tracesToFlush.map(trace => ({
        user_id: uid,
        event_type: 'request_trace',
        event_data: {
          request_id: trace.requestId,
          operation: trace.operation,
          duration: trace.duration,
          success: trace.success,
          error: trace.error,
          metadata: trace.metadata,
          parent_request_id: trace.parentRequestId,
          service: trace.service,
        },
        timestamp: new Date().toISOString(),
      }));

      const { error } = await client
        .from('analytics_events')
        .insert(events);

      if (error) {
        logger.error('Error flushing traces:', error);
        // Re-add to queue for retry
        this.traceQueue.unshift(...tracesToFlush);
      } else {
        logger.debug(`[Trace] Flushed ${tracesToFlush.length} traces to database`);
      }
    } catch (error) {
      logger.error('Error flushing traces:', error);
      // Re-add to queue for retry
      this.traceQueue.unshift(...tracesToFlush);
    }
  }

  /**
   * Start periodic flush
   */
  private startPeriodicFlush(): void {
    this.flushInterval = setInterval(() => {
      this.flushTraces();
    }, 5000); // Flush every 5 seconds
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get active traces count
   */
  getActiveTracesCount(): number {
    return this.activeTraces.size;
  }

  /**
   * Get queued traces count
   */
  getQueuedTracesCount(): number {
    return this.traceQueue.length;
  }
}

export const requestTracing = new RequestTracingService();

